import { useCallback, useEffect, useMemo, useRef } from 'react';
import Joyride, { ACTIONS, EVENTS, STATUS, LIFECYCLE } from 'react-joyride';
import { Nav } from '../components/icons/NavIcon';
import { useTour } from './tourContext';

/**
 * Premium spotlight overlay — the visual contract for the platform's tour
 * system. We delegate the heavy mechanics (positioning, scroll-into-view,
 * focus management, target waiting) to `react-joyride` and supply a fully
 * custom tooltip + heavy style overrides so the result reads as a native
 * piece of the TxnWeb editorial chrome, not a generic SaaS overlay.
 *
 * Visual contract:
 *   - Backdrop: rgba(15, 23, 42, 0.55) with a 2px blur, faded in over 150ms.
 *   - Spotlight: 4px navy ring + 12px outer glow; first beat pulses softly.
 *   - Tooltip card: 8px radius (matches `.radius-card` token), hairline
 *     navy border, restrained shadow, serif headline, 15px sans body.
 *   - Buttons: navy primary `Next` / `Done`, secondary `Back`, quiet `Skip`.
 *   - Keyboard: Esc closes, ←/→ navigates, Enter advances.
 *   - Motion: 200ms ease-out fade + 8px translateY entry; respects
 *     `prefers-reduced-motion`.
 */

const PRIMARY_NAVY = '#1E3A5F';
const PRIMARY_NAVY_DARK = '#16304F';

function PremiumTooltip(props) {
  const {
    backProps,
    closeProps,
    index,
    isLastStep,
    primaryProps,
    skipProps,
    step,
    tooltipProps,
    size,
  } = props;

  const { currentTour } = useTour();
  const stepDef = currentTour?.steps?.[index] || step;
  const total = size || currentTour?.steps?.length || 1;
  const tourName = currentTour?.name || 'Tour';
  const isFirst = index === 0;

  const headerCornerLabel = useMemo(() => {
    if (!total) return '';
    return `${tourName} · ${index + 1} / ${total}`;
  }, [tourName, index, total]);

  return (
    <div {...tooltipProps} className="txnweb-tour-tooltip" role="dialog" aria-modal="true">
      <div className="txnweb-tour-tooltip-header">
        <span className="txnweb-tour-tooltip-eyebrow">{headerCornerLabel}</span>
        <button
          type="button"
          {...closeProps}
          className="txnweb-tour-tooltip-close"
          aria-label="Close tour"
        >
          <Nav.x size={14} aria-hidden />
        </button>
      </div>

      {stepDef?.image ? (
        <div className="txnweb-tour-tooltip-illo">
          <img src={stepDef.image} alt="" aria-hidden loading="lazy" />
        </div>
      ) : null}

      <h2 className="txnweb-tour-tooltip-title">{stepDef?.title || step?.title}</h2>
      <p className="txnweb-tour-tooltip-body">{stepDef?.body || step?.content}</p>

      <div className="txnweb-tour-tooltip-progress" aria-hidden>
        {Array.from({ length: total }).map((_, i) => (
          <span
            key={i}
            className={`txnweb-tour-tooltip-dot ${i === index ? 'is-active' : ''} ${
              i < index ? 'is-past' : ''
            }`}
          />
        ))}
      </div>

      <div className="txnweb-tour-tooltip-actions">
        <button
          type="button"
          {...skipProps}
          className="txnweb-tour-tooltip-skip"
        >
          Skip tour
        </button>
        <div className="txnweb-tour-tooltip-nav">
          {!isFirst ? (
            <button type="button" {...backProps} className="txnweb-tour-tooltip-back">
              <Nav.arrowLeft size={14} aria-hidden /> Back
            </button>
          ) : (
            <span className="txnweb-tour-tooltip-step-counter tabular-nums">
              Step {index + 1} of {total}
            </span>
          )}
          <button type="button" {...primaryProps} className="txnweb-tour-tooltip-next">
            {isLastStep ? 'Done' : (
              <>
                Next <Nav.arrowRight size={14} aria-hidden />
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}

export default function TourSpotlight() {
  const { currentTour, stepIndex, advance, back, skip, end, isRunning } = useTour();
  const lastIndexRef = useRef(stepIndex);

  // Convert our tour catalog into the shape Joyride wants. We move the heavy
  // copy onto `target` + `disableBeacon`; the actual text is rendered by our
  // custom `PremiumTooltip`, which reads from the tour context (so we keep
  // markdown-style fidelity without going through Joyride's `content`).
  const joyrideSteps = useMemo(() => {
    if (!currentTour) return [];
    return currentTour.steps.map((s) => {
      // The HelpLauncher sits flush against the bottom-right viewport corner
      // (`position: fixed; right: 24px; bottom: 24px`). Any tooltip placement
      // that opens TOWARD the corner (right/bottom) or anchors to the
      // far-edge centre (`left`) ends up either clipped by the viewport or
      // pinned flush against the button with no breathing room. Force the
      // tooltip to open ABOVE the launcher anchored to its right edge so it
      // grows into the empty page area, not into the viewport edge.
      const isHelpLauncher = s.target === '[data-tour-target="help.launcher"]';
      const placement = isHelpLauncher ? 'top-end' : (s.placement || 'auto');
      return {
        target: s.target,
        content: s.body,
        title: s.title,
        placement,
        disableBeacon: true,
        hideCloseButton: false,
        spotlightPadding: placement === 'center' ? 0 : 8,
        spotlightClicks: s.spotlightClicks === true,
      };
    });
  }, [currentTour]);

  // Track step changes so we can run our own advance/back when Joyride asks.
  useEffect(() => {
    lastIndexRef.current = stepIndex;
  }, [stepIndex]);

  const handleCallback = useCallback(
    (data) => {
      const { action, status, type, lifecycle } = data;

      // Tour finished or operator skipped → fold back to provider.
      if (status === STATUS.FINISHED) {
        end({ outcome: 'completed' });
        return;
      }
      if (status === STATUS.SKIPPED) {
        skip();
        return;
      }
      if (action === ACTIONS.CLOSE && lifecycle === LIFECYCLE.COMPLETE) {
        end({ outcome: 'skipped' });
        return;
      }

      // If a target isn't on the page (e.g. user is on a different surface
      // than the tour expects, or a future tour references a target we
      // haven't wired yet) skip forward instead of getting stuck.
      if (type === EVENTS.TARGET_NOT_FOUND) {
        advance();
        return;
      }

      // Joyride dispatches `step:after` when the operator clicks Next / Back.
      if (type === EVENTS.STEP_AFTER) {
        if (action === ACTIONS.PREV) {
          back();
        } else if (action === ACTIONS.NEXT) {
          advance();
        }
      }
    },
    [advance, back, end, skip]
  );

  if (!isRunning || !currentTour) return null;

  return (
    <>
      <Joyride
        steps={joyrideSteps}
        stepIndex={stepIndex}
        run
        continuous
        showProgress={false}
        showSkipButton
        disableOverlayClose
        disableScrolling={false}
        spotlightClicks={joyrideSteps[stepIndex]?.spotlightClicks === true}
        scrollOffset={120}
        floaterProps={{
          // Pulls Floater closer to the spotlight so the tooltip never feels
          // disconnected from the highlighted element.
          disableAnimation: false,
          // Clamp the tooltip inside the viewport with 16px of breathing
          // room. Without this, steps that anchor to corner-pinned targets
          // (e.g. the HelpLauncher at bottom-right) push the tooltip past
          // the edge and clip the trailing close-X.
          options: {
            preventOverflow: {
              boundariesElement: 'window',
              padding: 16,
            },
          },
          // Slightly larger gap between the spotlight and the tooltip so the
          // close-X never lands flush against the highlighted target.
          offset: 12,
          styles: {
            arrow: { length: 8, spread: 16 },
            floater: { filter: 'drop-shadow(0 12px 32px rgba(15, 23, 42, 0.18))' },
          },
        }}
        tooltipComponent={PremiumTooltip}
        styles={{
          options: {
            arrowColor: '#ffffff',
            backgroundColor: '#ffffff',
            overlayColor: 'rgba(15, 23, 42, 0.55)',
            primaryColor: PRIMARY_NAVY,
            textColor: '#0F172A',
            zIndex: 9000,
          },
          spotlight: {
            borderRadius: 10,
            boxShadow:
              '0 0 0 4px rgba(30, 58, 95, 0.9), 0 0 0 12px rgba(30, 58, 95, 0.18), 0 24px 48px rgba(15, 23, 42, 0.28)',
          },
          spotlightLegacy: {
            borderRadius: 10,
          },
          // Note: we intentionally do NOT set `backdrop-filter: blur(...)` on
          // the overlay. react-joyride renders the dim + cutout as a single
          // SVG element using a `mask`, and `backdrop-filter` ignores SVG
          // masks — so any blur applied here blurs the *entire* page,
          // including the spotlighted target (the opposite of what we want).
          // The premium feel is carried by the navy dim + the cutout's ring,
          // glow and pulse; the blur is not worth the inversion bug.
        }}
        callback={handleCallback}
      />
    </>
  );
}

export { PremiumTooltip, PRIMARY_NAVY, PRIMARY_NAVY_DARK };
